import React, {useState} from "react";
import {Link, useParams} from "react-router-dom";
import {useQuery} from "@apollo/client";
import {
	AuthQueries, POLICIES_INDEX,
	POLICY_BY_KRN,
	POLICY_USAGES_BY_POLICY_KRN
} from "../../../backend/authorization/AuthQueries";
import {graphQLServices} from "../../../services/GraphQLServices";
import {
	Autocomplete,
	Box,
	Breadcrumbs, Checkbox,
	CircularProgress, FormControlLabel, Paper, Stack, TableHead,
	TextField,
	ToggleButton,
	ToggleButtonGroup,
	Typography
} from "@mui/material";
import {AuthPolicyRule} from "../../../backend/authorization/models/AuthPolicyRule";
import {AuthPolicy} from "../../../backend/authorization/models/AuthPolicy";
import {ListField} from "../../../components/common/input/ListField";
import {translateFunction} from "../../../localization/TranslationService";
import {ProcessButton} from "../../../components/common/input/ProcessButton";
import {PentaSplit} from "../../../components/common/PentaSplit";
import {AuthPolicyUsage} from "../../../backend/authorization/models/AuthPolicyUsage";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableRow from "@mui/material/TableRow";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import {isAuthorizedByPermission, pageAuthorizedByPermission} from "../../fragments/PageAuthorization";
import {permissions} from "../../../global/Permissions";
import {Delete} from "@mui/icons-material";
import {deletePolicy, putPolicy} from "../Functions";

const translate = translateFunction();

export const PolicyDetailsPage = pageAuthorizedByPermission(function () {
	const id = useParams()?.id ?? "";
	const parts = id.split(":") ?? [];
	const tenant = parts.length > 1 ? parts[0] : "kizi";
	const name = parts.length > 1 ? parts[1] : parts[0] ?? "";
	const krn = `krn:${tenant}:auth-policy:${name}`;
	
	const editable = isAuthorizedByPermission(permissions.authorizationPutPolicy);
	const deletable = isAuthorizedByPermission(permissions.authorizationDeletePolicy);
	
	const [statePolicy, setPolicy] = useState<AuthPolicy>();
	
	const policyQuery = useQuery<AuthQueries>(POLICY_BY_KRN, {
		client: graphQLServices.authorizationClient,
		variables: {krn},
		fetchPolicy: "no-cache"
	});
	
	const policiesIndexQuery = useQuery<AuthQueries>(POLICIES_INDEX, {
		client: graphQLServices.authorizationClient,
		fetchPolicy: "no-cache",
		variables: {
			where: {tenant: {eq: tenant}}
		}
	});
	
	const policyUsagesQuery = useQuery<AuthQueries>(POLICY_USAGES_BY_POLICY_KRN, {
		client: graphQLServices.authorizationClient,
		variables: {krn},
		skip: !statePolicy,
		fetchPolicy: "no-cache"
	});
	
	const error = policyQuery.error ?? policiesIndexQuery.error ?? policyUsagesQuery.error;
	if (error)
		throw error;
	
	if (policyQuery.loading)
		return <Box sx={{display: "flex", width: "100%", height: "100%"}}>
			<CircularProgress style={{margin: "auto"}}/>
		</Box>;
	
	const node = statePolicy ?? policyQuery.data?.policies.nodes[0];
	if (!node)
		throw new Error(`Policy with id '${id}' is not found.`);
	if (!statePolicy)
		setPolicy(node);
	const policy = node;
	
	const policiesIndex: AuthPolicy[] = policiesIndexQuery.data?.policies.nodes ?? [];
	const policyNamesById: Map<string, string> = policiesIndex.associateBy(it => it.krn, it => it.name);
	
	const policyUsages: AuthPolicyUsage[] | undefined = policyUsagesQuery.data?.policyUsages.nodes;
	
	return <PentaSplit
		top={<Breadcrumbs style={{margin: "5px"}}>
			<Link style={{color: "inherit", textDecoration: "unset"}}
				to="/authorization">{translate("Authorization")}</Link>
			<Link style={{color: "inherit", textDecoration: "unset"}}
				to="/authorization/policies">{translate("Policies")}</Link>
			<Typography color="text.primary">{id}</Typography>
		</Breadcrumbs>}
		center={<Stack spacing={1} style={{paddingBottom: "200px"}}>
			<Stack direction={"row"} justifyContent={"space-between"} style={{minHeight: "60px"}}>
				<ProcessButton
					startIcon={<Delete/>}
					defaultText={translate("Delete")}
					inProgressText={translate("Deleting")}
					completedText={translate("Deleted")}
					resetKeys={[policy]}
					variant={"outlined"}
					onClick={() => handleDelete(policy)}
					buttonProps={{
						disabled: !deletable
					}}
				/>
				<ProcessButton
					defaultText={translate("Save")}
					inProgressText={translate("Saving")}
					completedText={translate("Saved")}
					resetKeys={[policy]}
					variant={"outlined"}
					onClick={() => handleSave(policy)}
					buttonProps={{
						disabled: !editable
					}}
				/>
			</Stack>
			<TextField
				label={"Tenant"}
				value={policy.tenant}
				disabled
			/>
			<TextField
				label={"Name"}
				value={policy.name}
				disabled
			/>
			<FormControlLabel
				label={translate("Is synchronized")}
				control={<Checkbox
					checked={policy.isSynchronized}
					onChange={event => {
						setPolicy({...policy, isSynchronized: event.target.checked});
					}}
				/>}
			/>
			<ListField
				label={"Rules:"}
				value={policy.rules}
				setValue={value => setPolicy({...policy, rules: value})}
				render={props => <RuleField {...props}/>}
				newValue={() => ({actions: [], resources: []} as unknown as AuthPolicyRule)}
				disabled={!editable}
			/>
			<Autocomplete<AuthPolicy, true>
				multiple
				options={policiesIndex}
				disabled={!editable || policiesIndex.length === 0}
				isOptionEqualToValue={(option, value) => option.krn === value.krn}
				value={policy.subPolicies.map(krn => ({krn, name: policyNamesById.get(krn) ?? "..."} as AuthPolicy))}
				renderInput={(params) => <TextField
					{...params}
					label={translate("Sub-policies")}
					variant={"standard"}
				/>}
				onChange={(_, items) => {
					setPolicy({...policy, subPolicies: items.map(it => it.krn)});
				}}
				limitTags={3}
				disableCloseOnSelect
				getOptionLabel={(option) => option.name}
				getOptionDisabled={(option) => option.krn === krn}
				renderOption={(props, option, {selected}) => (
					<li {...props}>
						<FormControlLabel
							control={<Checkbox checked={selected}/>}
							label={option.name}
						/>
					</li>
				)}
			/>
			
			<Paper>
				<Typography paddingTop={"16px"} paddingLeft={"16px"} variant={"h6"}>Usages</Typography>
				{policyUsages === undefined
					? <Box sx={{display: "flex", width: "100%", height: "100%"}}>
						<CircularProgress style={{margin: "auto"}}/>
					</Box>
					: policyUsages.length === 0
						? <div style={{paddingBottom: "16px", paddingLeft: "16px"}}>None</div>
						: <TableContainer>
							<Table style={{tableLayout: "fixed"}}>
								<TableHead>
									<TableRow>
										<TableCell>Name</TableCell>
										<TableCell>Username</TableCell>
										<TableCell>Organization</TableCell>
										<TableCell>Role Name</TableCell>
									</TableRow>
								</TableHead>
								<TableBody>
									{policyUsages.map((usage, index) => {
										return <TableRow key={index}>
											<TableCell>{usage.name}</TableCell>
											<TableCell>{usage.userName ?? "-"}</TableCell>
											<TableCell>{usage.organisationId ?? "-"}</TableCell>
											<TableCell>{usage.roleName ?? "-"}</TableCell>
										</TableRow>;
									})}
								</TableBody>
							</Table>
						</TableContainer>}
			</Paper>
		</Stack>}
	/>;
}, [permissions.authorizationRead]);

async function handleSave(policy: AuthPolicy) {
	policy.rules.forEach((rule, index) => {
		const id = index + 1;
		if (!rule.effect)
			throw new Error("No effect for rule " + id);
		if (rule.actions.every(it => !it))
			throw new Error("No actions for rule " + id);
		if (rule.actions.some(it => !it))
			throw new Error("Some actions are empty for rule " + id);
		if (rule.resources.every(it => !it))
			throw new Error("No resources for rule " + id);
		if (rule.resources.some(it => !it))
			throw new Error("Some resources are empty for rule " + id);
	});
	
	await putPolicy(null, policy);
}

async function handleDelete(policy: AuthPolicy) {
	await deletePolicy(null, policy.krn);
}

interface RuleFieldProps {
	readonly value: AuthPolicyRule;
	readonly setValue: (rule: AuthPolicyRule) => void;
	readonly disabled?: boolean;
}

function RuleField(props: RuleFieldProps) {
	return <Stack direction={"row"} alignItems={"start"} spacing={2}
		style={{borderBottom: "1px solid", paddingLeft: "5px"}}>
		<div>
			<div>Effect:</div>
			<ToggleButtonGroup
				color="primary"
				value={props.value.effect}
				exclusive
				size={"small"}
				onChange={(event, value) => {
					props.setValue({...props.value, effect: value});
				}}
				disabled={props.disabled}
			>
				<ToggleButton value="ALLOW">Allow</ToggleButton>
				<ToggleButton value="DENY">Deny</ToggleButton>
			</ToggleButtonGroup>
		</div>
		<TextField
			label={"Actions"}
			value={props.value.actions.join("\n")}
			onChange={event => {
				const newList = event.target.value.split("\n").map(it => it.trim());
				const actions = newList.length === 1 && !newList[0]
					? []
					: newList;
				props.setValue({...props.value, actions});
			}}
			multiline
			disabled={props.disabled}
			style={{minWidth: "300px", marginTop: "10px", marginBottom: "10px"}}
		/>
		<TextField
			label={"Resources"}
			value={props.value.resources.join("\n")}
			onChange={event => {
				const newList = event.target.value.split("\n").map(it => it.trim());
				const resources = newList.length === 1 && !newList[0]
					? []
					: newList;
				props.setValue({...props.value, resources});
			}}
			multiline
			disabled={props.disabled}
			style={{minWidth: "300px", marginTop: "10px", marginBottom: "10px"}}
		/>
	</Stack>;
}

export function openAuthPolicyDetailsFor(krn: string) {
	window.location.href = linkAuthPolicyDetailsFor(krn);
}

export function linkAuthPolicyDetailsFor(krn: string): string {
	const parts = krn.split(":");
	const tenant = parts[1];
	const name = parts[3];
	const id = `${tenant}:${name}`;
	return `/authorization/policies/${encodeURIComponent(id)}`;
}
